<script setup>
import Section from "@/widgets/profile-drawer/ui/internal/Section.vue";
import ReadonlyField from "@/widgets/profile-drawer/ui/fields/ReadonlyField.vue";
import EditableField from "@/widgets/profile-drawer/ui/fields/EditableField.vue";

const props = defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  canEditContacts: { type: Boolean, default: false },
  canEditName: { type: Boolean, default: false },
});

const emit = defineEmits(["update"]);

function setField(key, value) {
  emit("update", key, value);
}

function formatPhone(value) {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "";
  const rest = (
    digits[0] === "8" ? digits.slice(1) : digits.replace(/^7/, "")
  ).slice(0, 10);
  let out = "+7";
  if (rest.length) out += ` (${rest.slice(0, 3)}`;
  if (rest.length >= 3) out += ")";
  if (rest.length > 3) out += ` ${rest.slice(3, 6)}`;
  if (rest.length > 6) out += `-${rest.slice(6, 8)}`;
  if (rest.length > 8) out += `-${rest.slice(8, 10)}`;
  return out;
}

function setPhone(value) {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return setField("phone", null);
  const normalized = digits[0] === "8" ? `7${digits.slice(1)}` : digits;
  setField("phone", `+${normalized.slice(0, 11)}`);
}

function onPhoneFocus() {
  if (!props.data.phone) setField("phone", "+7");
}
</script>

<template>
  <div
    class="grid gap-4 grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] max-[1100px]:grid-cols-1"
  >
    <div class="flex flex-col">
      <Section
        plain
        :title="$t('Основные данные')"
        :sub="
          canEditName
            ? $t('ФИО и идентификатор')
            : $t('ФИО и идентификатор (только администратор может изменить)')
        "
      >
        <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
          <EditableField
            v-if="editMode && canEditName"
            :label="$t('Фамилия')"
            :model-value="data.last_name"
            @update:model-value="setField('last_name', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Фамилия')"
            :value="data.last_name"
          />

          <EditableField
            v-if="editMode && canEditName"
            :label="$t('Имя')"
            :model-value="data.first_name"
            @update:model-value="setField('first_name', $event)"
          />
          <ReadonlyField v-else :label="$t('Имя')" :value="data.first_name" />

          <EditableField
            v-if="editMode && canEditName"
            :label="$t('Отчество')"
            :model-value="data.middle_name"
            @update:model-value="setField('middle_name', $event)"
          />
          <ReadonlyField
            v-else
            :label="$t('Отчество')"
            :value="data.middle_name"
          />

          <ReadonlyField :label="$t('ИИН')" :value="data.identifier" />
        </div>
      </Section>
    </div>

    <div class="flex flex-col">
      <Section plain :title="$t('Контакты')" :sub="$t('Email и телефон')">
        <div class="flex flex-col gap-y-3.5 gap-3.5">
          <EditableField
            v-if="editMode && canEditContacts"
            label="Email"
            type="email"
            placeholder="example@mail.com"
            :model-value="data.email"
            @update:model-value="setField('email', $event)"
          />
          <ReadonlyField v-else label="Email" :value="data.email" />

          <EditableField
            v-if="editMode && canEditContacts"
            :label="$t('Телефон')"
            :model-value="formatPhone(data.phone)"
            placeholder="+7 (___) ___-__-__"
            @focus="onPhoneFocus"
            @update:model-value="setPhone"
          />
          <ReadonlyField
            v-else
            :label="$t('Телефон')"
            :value="data.phone ? formatPhone(data.phone) : data.phone"
          />
        </div>
      </Section>
    </div>
  </div>
</template>
